import cx from "@hsl/core/utils/cx";

export interface Props {
    severity: "info" | "warning" | "danger" | "success";
    html?: string;
    className?: string;
    iconClassName?: string;
    withBorder?: boolean;
}

export const InfoMessage = ({
    severity,
    html,
    className,
    iconClassName,
    withBorder,
}: Props) => {
    const containerCName = cx(
        "w-full flex-row flex pl-2 py-2 pr-4",
        withBorder ? "sm:pl-4 sm:py-4 sm:pr-5" : "rounded",
        className,
    );
    const iconCName = cx(
        "w-4 h-4 mt-0.5 flex-shrink-0",
        withBorder ? "" : "ml-1 mr-2.5",
        iconClassName,
    );
    const textCName = cx(
        "flex-1 [&_a]:underline [&_a]:font-medium text-sm",
        withBorder && "sm:font-medium sm:text-base",
    );
    if (!html) {
        return null;
    }
    switch (severity) {
        case "info":
            return (
                <div
                    className={cx(
                        containerCName,
                        "text-dark-blue bg-super-blue-lightest",
                        withBorder && "border border-[#175EA126]",
                    )}
                >
                    <ExclamationCircleIcon className={iconCName} />
                    <div
                        className={textCName}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            );
        case "warning":
            return (
                <div
                    className={cx(
                        containerCName,
                        "text-ochre bg-dandelion-yellow-lightest",
                        withBorder && "border border-[#B65F1726]",
                    )}
                >
                    <ExclamationTriangleIcon className={iconCName} />
                    <div
                        className={textCName}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            );
        case "danger":
            return (
                <div
                    className={cx(
                        containerCName,
                        "text-poppy-red-darkest bg-poppy-red-lightest",
                        withBorder && "border border-[#67121026]",
                    )}
                >
                    <XCircleIcon className={iconCName} />
                    <div
                        className={textCName}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            );
        case "success":
            return (
                <div
                    className={cx(
                        containerCName,
                        "text-leafy-green-dark bg-leafy-green-lightest",
                        withBorder && "border border-[#00582626]",
                    )}
                >
                    <CheckCircleIcon className={iconCName} />
                    <div
                        className={textCName}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            );
    }
};

const ExclamationCircleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="25"
        viewBox="0 0 25 25"
        {...props}
    >
        <g id="information-filled" transform="translate(0.5 0.5)">
            <path
                id="Icon"
                d="M16,4A12,12,0,1,1,4,16,12,12,0,0,1,16,4Zm0,10a1.5,1.5,0,0,0-1.493,1.356L14.5,15.5v6a1.5,1.5,0,0,0,2.993.144L17.5,21.5v-6A1.5,1.5,0,0,0,16,14Zm0-5a1.5,1.5,0,1,0,1.5,1.5A1.5,1.5,0,0,0,16,9Z"
                transform="translate(-4 -4)"
                fill="#175ea1"
                stroke="rgba(23,94,161,0.15)"
                strokeWidth="1"
            />
        </g>
    </svg>
);

const ExclamationTriangleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        id="warning-filled"
        xmlns="http://www.w3.org/2000/svg"
        width="26"
        height="24"
        viewBox="0 0 26 24"
        {...props}
    >
        <path
            id="Icon"
            d="M17.181,4.331a2.271,2.271,0,0,1,.76.758L28.668,24.551A2.27,2.27,0,0,1,26.727,28H5.272a2.268,2.268,0,0,1-1.941-3.449L14.059,5.09A2.274,2.274,0,0,1,17.181,4.331ZM16,21.5A1.5,1.5,0,1,0,17.5,23,1.5,1.5,0,0,0,16,21.5Zm0-11A1.5,1.5,0,0,0,14.5,12v6a1.5,1.5,0,0,0,3,0V12A1.5,1.5,0,0,0,16,10.5Z"
            transform="translate(-3 -4)"
            fill="#b65f17"
        />
    </svg>
);

const XCircleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        id="crossmark-spot-filled"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        {...props}
    >
        <path
            id="Icon"
            d="M16,28A12,12,0,1,0,4,16,12,12,0,0,0,16,28ZM11.757,10.343,16,14.586l4.243-4.243a1,1,0,0,1,1.414,1.414L17.414,16l4.243,4.243a1,1,0,0,1-1.414,1.414L16,17.414l-4.243,4.243a1,1,0,0,1-1.414-1.414L14.586,16l-4.243-4.243a1,1,0,0,1,1.414-1.414Z"
            transform="translate(-4 -4)"
            fill="#671210"
        />
    </svg>
);

const CheckCircleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        id="checkmark-spot-filled"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        {...props}
    >
        <path
            id="Icon"
            d="M16,4A12,12,0,1,1,4,16,12,12,0,0,1,16,4Zm4.143,6.486-5.341,8.9-3.094-3.094a1,1,0,0,0-1.414,1.414L15.2,22.613l6.659-11.1a1,1,0,0,0-1.715-1.029Z"
            transform="translate(-4 -4)"
            fill="#005826"
        />
    </svg>
);
